import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api } from '../lib/api';
import { requireAccess } from '../lib/guards';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';

type Lohnklasse =
  | 'festanstellung'
  | 'teilzeit'
  | 'minijob'
  | 'freelance'
  | 'auszubildender'
  | 'praktikant'
  | 'sonstiges';

type Abteilung = 'haustechnik' | 'reinigung' | 'gaertner' | 'buero' | 'leitung';

interface Mitarbeiter {
  id: string;
  personalnummer: string | null;
  vorname: string;
  nachname: string;
  email: string | null;
  position: string | null;
  abteilung: Abteilung;
  lohnklasse: Lohnklasse | null;
  stundensatzIntern: string | null;
  eintrittsdatum: string;
  austrittsdatum: string | null;
  aktiv: boolean;
}

const LOHN_VALUES: Lohnklasse[] = [
  'festanstellung',
  'teilzeit',
  'minijob',
  'freelance',
  'auszubildender',
  'praktikant',
  'sonstiges',
];

const ABTEILUNGEN: { value: Abteilung; label: string }[] = [
  { value: 'haustechnik', label: 'Haustechnik' },
  { value: 'reinigung', label: 'Reinigung' },
  { value: 'gaertner', label: 'Gartenbau' },
  { value: 'buero', label: 'Buero' },
  { value: 'leitung', label: 'Leitung' },
];

export const Route = createFileRoute('/mitarbeiter/')({
  beforeLoad: () => requireAccess('/api/v1/mitarbeiter'),
  component: MitarbeiterPage,
});

function MitarbeiterPage() {
  const qc = useQueryClient();
  const [aktivOnly, setAktivOnly] = useState(true);
  const list = useQuery({
    queryKey: ['mitarbeiter', aktivOnly],
    queryFn: () =>
      api<{ items: Mitarbeiter[] }>(`/api/v1/mitarbeiter${aktivOnly ? '?aktiv=true' : ''}`),
  });
  const [showForm, setShowForm] = useState(false);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Mitarbeiter"
        description="HR-Stammdaten (getrennt vom Auth-User). Austritt = Datum setzen + aktiv auf false."
        actions={
          <Button onClick={() => setShowForm((s) => !s)} variant={showForm ? 'ghost' : 'primary'}>
            {showForm ? 'Abbrechen' : '+ Neuer Mitarbeiter'}
          </Button>
        }
      />

      <label className="inline-flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={aktivOnly}
          onChange={(e) => setAktivOnly(e.target.checked)}
        />
        nur aktive anzeigen
      </label>

      {showForm && (
        <MitarbeiterCreateForm
          onDone={() => {
            setShowForm(false);
            void qc.invalidateQueries({ queryKey: ['mitarbeiter'] });
          }}
        />
      )}

      <ErrorBanner error={list.error} />

      {list.data && (
        <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2 text-left w-28">Pers-Nr.</th>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Position</th>
                <th className="px-4 py-2 text-left w-32">Abteilung</th>
                <th className="px-4 py-2 text-left w-36">Lohnklasse</th>
                <th className="px-4 py-2 text-left w-28">Eintritt</th>
                <th className="px-4 py-2 text-left w-28">Austritt</th>
                <th className="px-4 py-2 text-center w-20">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.data.items.map((m) => (
                <MitarbeiterRow key={m.id} m={m} />
              ))}
              {list.data.items.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-8 text-center text-slate-400">
                    Keine Mitarbeiter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MitarbeiterCreateForm({ onDone }: { onDone: () => void }) {
  const [st, setSt] = useState({
    personalnummer: '',
    vorname: '',
    nachname: '',
    email: '',
    position: '',
    abteilung: 'haustechnik' as Abteilung,
    lohnklasse: '' as '' | Lohnklasse,
    stundensatzIntern: '',
    eintrittsdatum: new Date().toISOString().slice(0, 10),
  });
  const [error, setError] = useState<unknown>(null);
  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        vorname: st.vorname,
        nachname: st.nachname,
        eintrittsdatum: st.eintrittsdatum,
        abteilung: st.abteilung,
      };
      if (st.personalnummer.trim()) body['personalnummer'] = st.personalnummer.trim();
      if (st.email.trim()) body['email'] = st.email.trim();
      if (st.position.trim()) body['position'] = st.position.trim();
      if (st.lohnklasse) body['lohnklasse'] = st.lohnklasse;
      if (st.stundensatzIntern.trim()) body['stundensatzIntern'] = Number(st.stundensatzIntern);
      return api<{ item: Mitarbeiter }>('/api/v1/mitarbeiter', { method: 'POST', body });
    },
    onSuccess: () => onDone(),
    onError: (err) => setError(err),
  });
  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    save.mutate();
  }
  return (
    <form
      onSubmit={onSubmit}
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3 shadow-sm"
    >
      <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
        <div>
          <label className="text-xs font-medium text-slate-600">Pers-Nr.</label>
          <Input
            value={st.personalnummer}
            onChange={(e) => setSt({ ...st, personalnummer: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Vorname</label>
          <Input
            required
            value={st.vorname}
            onChange={(e) => setSt({ ...st, vorname: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Nachname</label>
          <Input
            required
            value={st.nachname}
            onChange={(e) => setSt({ ...st, nachname: e.target.value })}
          />
        </div>
        <div className="md:col-span-2">
          <label className="text-xs font-medium text-slate-600">E-Mail</label>
          <Input
            type="email"
            value={st.email}
            onChange={(e) => setSt({ ...st, email: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Eintritt</label>
          <Input
            required
            type="date"
            value={st.eintrittsdatum}
            onChange={(e) => setSt({ ...st, eintrittsdatum: e.target.value })}
          />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Position</label>
          <Input value={st.position} onChange={(e) => setSt({ ...st, position: e.target.value })} />
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Abteilung *</label>
          <Select
            required
            value={st.abteilung}
            onChange={(e) => setSt({ ...st, abteilung: e.target.value as Abteilung })}
          >
            {ABTEILUNGEN.map((a) => (
              <option key={a.value} value={a.value}>
                {a.label}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Lohnklasse</label>
          <Select
            value={st.lohnklasse}
            onChange={(e) => setSt({ ...st, lohnklasse: e.target.value as Lohnklasse | '' })}
          >
            <option value="">– wählen –</option>
            {LOHN_VALUES.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="text-xs font-medium text-slate-600">Stundensatz intern (€)</label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={st.stundensatzIntern}
            onChange={(e) => setSt({ ...st, stundensatzIntern: e.target.value })}
          />
        </div>
      </div>
      <ErrorBanner error={error} />
      <div className="flex justify-end">
        <Button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Speichere…' : 'Mitarbeiter anlegen'}
        </Button>
      </div>
    </form>
  );
}

function MitarbeiterRow({ m }: { m: Mitarbeiter }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [position, setPosition] = useState(m.position ?? '');
  const [abteilung, setAbteilung] = useState<Abteilung>(m.abteilung);
  const [austritt, setAustritt] = useState(m.austrittsdatum ?? '');
  const [aktiv, setAktiv] = useState(m.aktiv);
  const [error, setError] = useState<unknown>(null);

  const save = useMutation({
    mutationFn: async () => {
      const patch: Record<string, unknown> = {};
      if (position !== (m.position ?? '')) patch['position'] = position;
      if (abteilung !== m.abteilung) patch['abteilung'] = abteilung;
      if (austritt !== (m.austrittsdatum ?? ''))
        patch['austrittsdatum'] = austritt === '' ? null : austritt;
      if (aktiv !== m.aktiv) patch['aktiv'] = aktiv;
      if (Object.keys(patch).length === 0) return m;
      return api<{ item: Mitarbeiter }>(`/api/v1/mitarbeiter/${m.id}`, {
        method: 'PATCH',
        body: patch,
      });
    },
    onSuccess: () => {
      setEditing(false);
      void qc.invalidateQueries({ queryKey: ['mitarbeiter'] });
    },
    onError: (err) => setError(err),
  });

  if (!editing) {
    return (
      <tr className="text-sm text-slate-800">
        <td className="px-4 py-2 font-mono text-xs text-slate-600">{m.personalnummer ?? '–'}</td>
        <td className="px-4 py-2 font-medium">
          <Link
            to="/mitarbeiter/$id"
            params={{ id: m.id }}
            className="text-brand-700 hover:underline"
          >
            {m.vorname} {m.nachname}
          </Link>
          {m.email && <div className="text-xs text-slate-500 font-normal">{m.email}</div>}
        </td>
        <td className="px-4 py-2">{m.position ?? '–'}</td>
        <td className="px-4 py-2 text-slate-600">
          {ABTEILUNGEN.find((a) => a.value === m.abteilung)?.label ?? m.abteilung}
        </td>
        <td className="px-4 py-2 text-slate-600">{m.lohnklasse ?? '–'}</td>
        <td className="px-4 py-2 text-slate-600">{m.eintrittsdatum}</td>
        <td className="px-4 py-2 text-slate-600">{m.austrittsdatum ?? '–'}</td>
        <td className="px-4 py-2 text-center">
          <span
            className={`inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium ${
              m.aktiv
                ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                : 'bg-slate-100 text-slate-500 border-slate-200'
            }`}
          >
            {m.aktiv ? 'aktiv' : 'inaktiv'}
          </span>
        </td>
        <td className="px-4 py-2 text-right">
          <Button variant="ghost" onClick={() => setEditing(true)}>
            Bearbeiten
          </Button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="text-sm bg-brand-50/50">
      <td className="px-4 py-2 font-mono text-xs text-slate-600">{m.personalnummer ?? '–'}</td>
      <td className="px-4 py-2">
        <div className="font-medium">
          {m.vorname} {m.nachname}
        </div>
      </td>
      <td className="px-4 py-2">
        <Input value={position} onChange={(e) => setPosition(e.target.value)} />
      </td>
      <td className="px-4 py-2">
        <Select value={abteilung} onChange={(e) => setAbteilung(e.target.value as Abteilung)}>
          {ABTEILUNGEN.map((a) => (
            <option key={a.value} value={a.value}>
              {a.label}
            </option>
          ))}
        </Select>
      </td>
      <td className="px-4 py-2 text-slate-600">{m.lohnklasse ?? '–'}</td>
      <td className="px-4 py-2 text-slate-600">{m.eintrittsdatum}</td>
      <td className="px-4 py-2">
        <Input type="date" value={austritt} onChange={(e) => setAustritt(e.target.value)} />
      </td>
      <td className="px-4 py-2 text-center">
        <input type="checkbox" checked={aktiv} onChange={(e) => setAktiv(e.target.checked)} />
      </td>
      <td className="px-4 py-2">
        <div className="flex items-center justify-end gap-2">
          {error instanceof Error && <span className="text-xs text-red-600">{error.message}</span>}
          <Button variant="ghost" onClick={() => setEditing(false)} disabled={save.isPending}>
            Abbrechen
          </Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Speichere…' : 'Speichern'}
          </Button>
        </div>
      </td>
    </tr>
  );
}
